import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Formatters, Messages } from '../../../../../gen-src/Localize';
import { VirtualConnectionTableOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createLabelColumnFormatter,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  ExplorerStyledDisplayGrid,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  reportGraphQLErrors,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import {
  GlobalAssetGridDC3ColumnFactoryParams,
} from '../sorting/makeColumnFactoryParams';
import {
  VirtualConnectionTablesValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionTablesDisplayGridRowData } from '../table/types/VirtualConnectionTablesDisplayGridRowData';

const shouldBeObfuscated: (rowData: VirtualConnectionTablesDisplayGridRowData) => boolean = ({
  name,
}) => name === null;

type TablesColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<VirtualConnectionTablesValidSortFields>,
  VirtualConnectionTablesDisplayGridRowData,
  VirtualConnectionTablesValidSortFields
>;

function columns(siteUrls: SiteUrls): ReadonlyArray<TablesColumnFactory> {
  const columns: ReadonlyArray<TablesColumnFactory> = [
    () => createLabelGridColumnFormatter(siteUrls, 'CERTIFICATION'),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (params) => ({
      name: 'name',
      dataKey: VirtualConnectionTableOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(params),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.virtualConnectionTableLineagePath(vizportalUrlId),
        undefined, // noPadding
        true // disableTabIndex
      ),
      growToFit: true,
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: VirtualConnectionTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamWorkbooksConnection }) =>
          downstreamWorkbooksConnection
            ? Formatters.formatCount(downstreamWorkbooksConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: VirtualConnectionTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createTextCellFormatter(
        ({ downstreamDatasourcesConnection }) =>
          downstreamDatasourcesConnection
            ? Formatters.formatCount(downstreamDatasourcesConnection.totalCount)
            : null
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'extractStatus',
      dataKey: UnsortableColumnKey,
      cellFormatter: createTextCellFormatter(
        ({ isExtracted }) =>
          isExtracted
            ? Messages.ExtractConnection()
            : Messages.LiveConnection()
      ),
      headerText: Messages.GridHeaderConnectionStatus(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      sortable: false,
    }),
  ];

  return columns.filter(Boolean) as ReadonlyArray<TablesColumnFactory>;

}


export type VirtualConnectionTablesDisplayGridProps = UsualDisplayGridProps<
  VirtualConnectionTablesDisplayGridRowData,
  GraphQLError
> &
  GridSortProps<VirtualConnectionTablesValidSortFields>;

export function VirtualConnectionTablesDisplayGrid(props: VirtualConnectionTablesDisplayGridProps): JSX.Element {
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls).map((columnFactory) => columnFactory(props));
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: VirtualConnectionTablesDisplayGridRowData, urls: SiteUrls) {
  return {
    contentId: rowData.id,
    contentLuid: rowData.luid,
    contentType: rowData.contentType,
    contentName: rowData.name ?? undefined,
    contentUrl: urls.virtualConnectionTableLineageUrl(rowData.vizportalUrlId),
    subjectArea: rowData.__typename
  };
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']):
  TypedColumn<VirtualConnectionTablesDisplayGridRowData, VirtualConnectionTablesValidSortFields> {

  function getPropsFromRowData(rowData: VirtualConnectionTablesDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'virtualconnection-tables', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<VirtualConnectionTablesDisplayGridRowData, VirtualConnectionTablesValidSortFields>;
}
